import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link2, Copy, Check, QrCode, ArrowRight, Loader2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Card, CardContent } from '@/client/components/ui/card';
import { useUIStore } from '@/client/stores/ui-store';
import { apiFetch } from '@/client/lib/api';
import { cn } from '@/client/lib/utils';

interface CreatedLink {
  id: string;
  shortCode: string;
  shortUrl: string;
  destinationUrl: string;
  title?: string;
}

/**
 * QuickCreate - Inline URL shortening component for the dashboard
 *
 * Enables the "30-second win": paste URL → get short link → copy
 * No modal, no navigation, minimal friction.
 */
export function QuickCreate() {
  const [url, setUrl] = useState('');
  const [result, setResult] = useState<CreatedLink | null>(null);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { openCreateLinkModal } = useUIStore();

  // Focus input on mount (desktop only)
  useEffect(() => {
    // Don't auto-focus on mobile - prevents keyboard from popping up
    const isMobile = window.matchMedia('(max-width: 640px)').matches;
    if (!isMobile && inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const createLinkMutation = useMutation({
    mutationFn: async (destinationUrl: string) => {
      const response = await apiFetch('/api/links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: destinationUrl }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to create link' }));
        throw new Error(error.error || error.message || 'Failed to create link');
      }

      return response.json() as Promise<CreatedLink>;
    },
    onSuccess: (data) => {
      setResult(data);
      setUrl('');
      // Invalidate queries to refresh dashboard stats and links list
      queryClient.invalidateQueries({ queryKey: ['links'] });

      // Fire-and-forget title extraction (runs in background)
      // This improves the link's metadata without blocking the user
      apiFetch(`/api/links/${data.shortCode}/extract-title`, {
        method: 'POST',
      })
        .then((res) => res.json())
        .then((titleData: { title?: string; extracted?: boolean }) => {
          if (titleData.extracted && titleData.title) {
            // Update result with extracted title
            setResult((prev) => (prev ? { ...prev, title: titleData.title } : prev));
            // Refresh links list to show title
            queryClient.invalidateQueries({ queryKey: ['links'] });
          }
        })
        .catch(() => {
          // Silent failure - title extraction is optional
        });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    createLinkMutation.mutate(url.trim());
  };

  const copyToClipboard = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.shortUrl);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const resetForm = () => {
    setResult(null);
    setCopied(false);
    // Focus input after reset
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const openQRCode = () => {
    if (!result) return;
    // Navigate to the link detail page which has QR code
    window.open(`/links/${result.shortCode}`, '_blank');
  };

  // Result state - show the created link
  if (result) {
    return (
      <Card className="rounded-2xl border-0 overflow-hidden bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col gap-4">
            {/* Success header */}
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <div className="size-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Check className="size-4" />
              </div>
              <span className="font-medium truncate">{result.title || 'Link created!'}</span>
            </div>

            {/* Short URL with copy button */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-xl bg-white dark:bg-black/20 border border-emerald-200 dark:border-emerald-800">
                <Link2 className="size-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                <code className="flex-1 font-mono text-sm sm:text-base font-semibold truncate">
                  {result.shortUrl.replace('https://', '')}
                </code>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={copyToClipboard}
                  className={cn(
                    'flex-1 sm:flex-none rounded-xl gap-2 transition-colors',
                    copied
                      ? 'bg-emerald-500 hover:bg-emerald-500 text-white'
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  )}
                >
                  {copied ? (
                    <>
                      <Check className="size-4" />
                      <span className="sm:inline">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="size-4" />
                      <span className="sm:inline">Copy</span>
                    </>
                  )}
                </Button>

                <Button
                  onClick={openQRCode}
                  variant="outline"
                  className="rounded-xl border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                >
                  <QrCode className="size-4" />
                </Button>
              </div>
            </div>

            {/* Destination URL preview */}
            <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
              <ExternalLink className="size-3 shrink-0" />
              <span className="truncate">{result.destinationUrl}</span>
            </p>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-emerald-200 dark:border-emerald-800">
              <Link
                to={`/links/${result.shortCode}`}
                className="text-sm text-emerald-700 dark:text-emerald-400 hover:underline"
              >
                View details & analytics
              </Link>
              <span className="text-muted-foreground">·</span>
              <button
                onClick={resetForm}
                className="text-sm text-emerald-700 dark:text-emerald-400 hover:underline"
              >
                Create another
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Input state - show the URL input form
  return (
    <Card className="rounded-2xl border-0 overflow-hidden bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
      <CardContent className="p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Input row */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground pointer-events-none" />
              <Input
                ref={inputRef}
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Paste a URL to shorten..."
                className="pl-12 h-12 sm:h-14 text-base sm:text-lg rounded-xl border-amber-200 dark:border-amber-800 bg-white dark:bg-black/20 focus-visible:ring-amber-500"
                disabled={createLinkMutation.isPending}
              />
            </div>

            <Button
              type="submit"
              disabled={!url.trim() || createLinkMutation.isPending}
              size="lg"
              className="h-12 sm:h-14 px-6 sm:px-8 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-semibold gap-2"
            >
              {createLinkMutation.isPending ? (
                <>
                  <Loader2 className="size-5 animate-spin" />
                  <span className="hidden sm:inline">Creating...</span>
                </>
              ) : (
                <>
                  <span>Shorten</span>
                  <ArrowRight className="size-5" />
                </>
              )}
            </Button>
          </div>

          {/* More options link */}
          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => {
                // Pre-fill the modal with the URL if one is entered
                if (url.trim()) {
                  // Store URL in session storage for modal to pick up
                  sessionStorage.setItem('quickCreateUrl', url.trim());
                }
                openCreateLinkModal();
              }}
              className="text-muted-foreground hover:text-foreground hover:underline transition-colors"
            >
              + Add title, custom code, tags...
            </button>

            <span className="text-muted-foreground hidden sm:inline">
              Press <kbd className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono">⌘K</kbd>{' '}
              anywhere
            </span>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
